// Animated wind-particle overlay, Canvas 2D. Particles live in lon/lat
// (not pixel) space so they stay put when the user pans/zooms; each frame
// we sample the wind vector at their location via bilinear interpolation,
// advance them, and re-project to pixels for drawing.
//
// Trail fade uses `destination-out` composite with a low-alpha black
// rectangle — erodes existing pixel alpha a few percent per frame so
// trails gracefully disappear instead of painting solid streaks.

import type { WindGrid } from './api';

interface Particle {
  lon: number;
  lat: number;
  age: number;
}

export interface WindParticleOpts {
  particleCount?: number;
  maxAge?: number;
  stepSeconds?: number;
  // Per-frame trail fade alpha (0..1). Lower = trails last longer and
  // direction reads more clearly. 0.02 is a good middle; 0.008 gives very
  // long streaky trails; 0.05+ looks like stippled dots.
  fadeAlpha?: number;
  // Project (lon, lat) → pixel coords relative to the canvas. Returns null
  // when off-canvas or the map isn't available.
  projectToPixel: (lon: number, lat: number) => [number, number] | null;
}

export class WindParticleSystem {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private grid: WindGrid;
  private particles: Particle[] = [];
  private rafId = 0;
  private running = false;
  private opts: Required<Omit<WindParticleOpts, 'projectToPixel'>> & {
    projectToPixel: WindParticleOpts['projectToPixel'];
  };

  constructor(canvas: HTMLCanvasElement, grid: WindGrid, opts: WindParticleOpts) {
    this.canvas = canvas;
    const c = canvas.getContext('2d');
    if (!c) throw new Error('Canvas 2D context unavailable');
    this.ctx = c;
    this.grid = grid;
    this.opts = {
      particleCount: opts.particleCount ?? 1600,
      maxAge: opts.maxAge ?? 450,
      stepSeconds: opts.stepSeconds ?? 0.9,
      fadeAlpha: opts.fadeAlpha ?? 0.006,
      projectToPixel: opts.projectToPixel,
    };
    for (let i = 0; i < this.opts.particleCount; i++) {
      this.particles.push(this.spawn(Math.random() * this.opts.maxAge));
    }
  }

  setGrid(grid: WindGrid) {
    this.grid = grid;
  }

  start() {
    if (this.running) return;
    this.running = true;
    const loop = () => {
      if (!this.running) return;
      this.step();
      this.rafId = requestAnimationFrame(loop);
    };
    loop();
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private spawn(initialAge = 0): Particle {
    return {
      lon: this.grid.lonMin + Math.random() * (this.grid.lonMax - this.grid.lonMin),
      lat: this.grid.latMin + Math.random() * (this.grid.latMax - this.grid.latMin),
      age: initialAge,
    };
  }

  private sampleUV(lon: number, lat: number): [number, number] {
    const fx = ((lon - this.grid.lonMin) / (this.grid.lonMax - this.grid.lonMin)) * (this.grid.nLon - 1);
    const fy = ((lat - this.grid.latMin) / (this.grid.latMax - this.grid.latMin)) * (this.grid.nLat - 1);
    if (fx < 0 || fy < 0 || fx > this.grid.nLon - 1 || fy > this.grid.nLat - 1) return [0, 0];
    const x0 = Math.floor(fx);
    const x1 = Math.min(x0 + 1, this.grid.nLon - 1);
    const y0 = Math.floor(fy);
    const y1 = Math.min(y0 + 1, this.grid.nLat - 1);
    const tx = fx - x0;
    const ty = fy - y0;
    const idx = (y: number, x: number) => y * this.grid.nLon + x;
    const u00 = this.grid.u[idx(y0, x0)];
    const u01 = this.grid.u[idx(y0, x1)];
    const u10 = this.grid.u[idx(y1, x0)];
    const u11 = this.grid.u[idx(y1, x1)];
    const v00 = this.grid.v[idx(y0, x0)];
    const v01 = this.grid.v[idx(y0, x1)];
    const v10 = this.grid.v[idx(y1, x0)];
    const v11 = this.grid.v[idx(y1, x1)];
    const u = (1 - tx) * ((1 - ty) * u00 + ty * u10) + tx * ((1 - ty) * u01 + ty * u11);
    const v = (1 - tx) * ((1 - ty) * v00 + ty * v10) + tx * ((1 - ty) * v01 + ty * v11);
    return [u, v];
  }

  private step() {
    const { ctx, canvas } = this;
    // Fade existing pixels so trails decay. Lower alpha = longer trails.
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = `rgba(0, 0, 0, ${this.opts.fadeAlpha})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';

    ctx.lineCap = 'round';
    ctx.lineWidth = 1.8;

    const dt = this.opts.stepSeconds;
    const maxAge = this.opts.maxAge;

    for (const p of this.particles) {
      const [uMs, vMs] = this.sampleUV(p.lon, p.lat);
      const metersPerLat = 111000;
      const metersPerLon = 111000 * Math.cos((p.lat * Math.PI) / 180);
      const newLon = p.lon + (uMs * dt) / metersPerLon;
      const newLat = p.lat + (vMs * dt) / metersPerLat;

      const from = this.opts.projectToPixel(p.lon, p.lat);
      const to = this.opts.projectToPixel(newLon, newLat);
      const speed = Math.hypot(uMs, vMs);

      if (from && to && p.age < maxAge) {
        // Push alpha high so trails read clearly against the light CARTO
        // basemap. Gentle speed-based lift so faster wind still feels more
        // energetic without drowning the slow-wind signal.
        const alpha = Math.min(1, 0.75 + speed / 20);
        ctx.strokeStyle = colorForSpeed(speed, alpha);
        ctx.beginPath();
        ctx.moveTo(from[0], from[1]);
        ctx.lineTo(to[0], to[1]);
        ctx.stroke();
      }

      p.lon = newLon;
      p.lat = newLat;
      p.age++;

      if (
        p.age > maxAge ||
        p.lon < this.grid.lonMin ||
        p.lon > this.grid.lonMax ||
        p.lat < this.grid.latMin ||
        p.lat > this.grid.latMax
      ) {
        Object.assign(p, this.spawn());
      }
    }
  }
}

// Speed color: deep indigo (calm) → teal → green → orange → red (gale).
// Stops tuned so calm winds are visible against a light basemap. Exported
// so the WindMap legend can render the exact same palette — source of truth
// for color ↔ speed mapping.
export const WIND_COLOR_STOPS: Array<[number, [number, number, number]]> = [
  [0, [30, 80, 180]],
  [6, [30, 140, 180]],
  [12, [30, 160, 90]],
  [20, [210, 180, 40]],
  [30, [220, 110, 40]],
  [45, [200, 40, 50]],
];

export function colorForMph(mph: number, alpha = 1): string {
  let c: [number, number, number] = WIND_COLOR_STOPS[0][1];
  for (let i = 1; i < WIND_COLOR_STOPS.length; i++) {
    const [s1, col1] = WIND_COLOR_STOPS[i];
    if (mph <= s1) {
      const [s0, col0] = WIND_COLOR_STOPS[i - 1];
      const t = (mph - s0) / (s1 - s0);
      c = [
        col0[0] + t * (col1[0] - col0[0]),
        col0[1] + t * (col1[1] - col0[1]),
        col0[2] + t * (col1[2] - col0[2]),
      ];
      break;
    }
    c = col1;
  }
  return `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${alpha})`;
}

function colorForSpeed(msSpeed: number, alpha: number): string {
  return colorForMph(msSpeed * 2.23694, alpha);
}
